import React, { Fragment } from 'react';
import uniqueId from 'lodash/uniqueId';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import $metrics from 'app/stores/metrics/$metrics';
import { buildFilterGroup } from 'core/util/filters';
import {
  MAX_KEYS_PER_POLICY,
  EVENT_POLICY_TYPES,
  COMPARISON_DIRECTIONS,
  POLICY_BASELINE_PRESET_TYPES
} from '@kentik/ui-shared/alerting/constants';
import { checkApplication, getTopLevelApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import $auth from 'app/stores/$auth';

export function serializeBaselinePercentValue(decrease) {
  const value = Number(decrease);
  if (value >= 100) {
    return 10000;
  }
  return (1 / (100 - value)) * 10000 - 100;
}

export function deserializeBaselinePercentValue(increase) {
  const value = Number(increase);
  return Math.round((100 * value) / (value + 100));
}

export function displayRawBaselinePercentValue(rawValue, direction) {
  if (direction === COMPARISON_DIRECTIONS.TO_HISTORY) {
    return `${rawValue}% above baseline`;
  }

  return `${rawValue}% below baseline`;
}

export function getPolicyExpireDate(days) {
  return moment().utc().add(days, 'days').toISOString();
}

export function isSilentMode(expireDate) {
  return expireDate ? moment(expireDate).isAfter() : false;
}

export function getSilentModeDate(expireDate) {
  return isSilentMode(expireDate) ? moment.utc(expireDate).toISOString() : moment.utc().add(7, 'days').toISOString();
}

export function hasSubMinuteEvaluationPeriod(evaluationPeriod) {
  if (!evaluationPeriod) {
    return false;
  }
  if (typeof evaluationPeriod === 'string') {
    return Number(evaluationPeriod.replace('s', '')) < 60;
  }
  return evaluationPeriod < 60;
}

export function formatEvaluationPeriod(evaluationPeriod) {
  return typeof evaluationPeriod === 'string'
    ? evaluationPeriod.replace('s', ' seconds')
    : `${evaluationPeriod} seconds`;
}

/**
 * Preset   Percentile   Keys  Baseline Window Start   Baseline Trailing Window    Baseline minimum data
 * Express     98th       25           1 hour                   1 weeks                 2 Days
 * Default     98th       25           1 day                    4 weeks                 4 Days
 * Precision   98th      300           1 day                    4 weeks                 28 Days
 */

/**
 Preset    Comparison Data (bucket) 	Baseline derivation 	Baseline Precision	Toggle: Weekdays
 Express      every hour                current baseline	         98th	              No
 Default      every hour                current baseline 	         98th	              No
 Precision    every hour                current baseline	         98th             	No
 */

export function getExpressBaselineOverallSettingValues() {
  return {
    nTopKeysAddedToBaseline: '25',
    nTopKeysEvaluated: '25',
    'baselineSettings.datapointAggregationMethod': 'REL_P98',
    'baselineSettings.startOffset': 3600,
    'baselineSettings.length': 604800,
    'baselineSettings.minimalLength': 2,
    'baselineSettings.minimal_length_unit': 'days',
    'baselineSettings.seasonality': 'hourly',
    'baselineSettings.neighbourhoodAggregationMethod': '',
    'baselineSettings.neighbourhoodRadius': 1,
    'baselineSettings.comparisonValueAggregateFunction': 'REL_P95',
    'baselineSettings.weekendAware': false
  };
}

export function getDefaultBaselineOverallSettingValues() {
  return {
    nTopKeysAddedToBaseline: '25',
    nTopKeysEvaluated: '25',
    'baselineSettings.datapointAggregationMethod': 'REL_P98',
    'baselineSettings.startOffset': 86400,
    'baselineSettings.length': 2419200,
    'baselineSettings.minimalLength': 4,
    'baselineSettings.minimal_length_unit': 'days',
    'baselineSettings.seasonality': 'daily',
    'baselineSettings.neighbourhoodAggregationMethod': '',
    'baselineSettings.neighbourhoodRadius': 1,
    'baselineSettings.comparisonValueAggregateFunction': 'REL_P95',
    'baselineSettings.weekendAware': false
  };
}

export function getPrecisionBaselineOverallSettingValues() {
  return {
    nTopKeysAddedToBaseline: '300',
    nTopKeysEvaluated: '300',
    'baselineSettings.datapointAggregationMethod': 'REL_P98',
    'baselineSettings.startOffset': 86400,
    'baselineSettings.length': 2419200,
    'baselineSettings.minimalLength': 14,
    'baselineSettings.minimal_length_unit': 'days',
    'baselineSettings.seasonality': 'daily',
    'baselineSettings.neighbourhoodAggregationMethod': '',
    'baselineSettings.neighbourhoodRadius': 1,
    'baselineSettings.comparisonValueAggregateFunction': 'REL_P95',
    'baselineSettings.weekendAware': false
  };
}

export function getBaselineOverallSettingValues(overallSetting) {
  switch (overallSetting) {
    case POLICY_BASELINE_PRESET_TYPES.EXPRESS:
      return getExpressBaselineOverallSettingValues();
    case POLICY_BASELINE_PRESET_TYPES.DEFAULT:
      return getDefaultBaselineOverallSettingValues();
    case POLICY_BASELINE_PRESET_TYPES.PRECISION:
      return getPrecisionBaselineOverallSettingValues();
    default:
      return {};
  }
}

export function doesPolicyBaselineMatchDefaults(policy, defaults) {
  const policyBaselineValues = {
    nTopKeysAddedToBaseline: `${policy.nTopKeysAddedToBaseline}`,
    nTopKeysEvaluated: `${policy.nTopKeysEvaluated}`,
    'baselineSettings.datapointAggregationMethod': policy.baselineSettings.datapointAggregationMethod,
    'baselineSettings.startOffset': policy.baselineSettings.startOffset,
    'baselineSettings.length': policy.baselineSettings.length,
    'baselineSettings.minimalLength': policy.baselineSettings.minimalLength,
    'baselineSettings.minimal_length_unit': policy.baselineSettings.minimal_length_unit,
    'baselineSettings.seasonality': policy.baselineSettings.seasonality,
    'baselineSettings.neighbourhoodAggregationMethod': policy.baselineSettings.neighbourhoodAggregationMethod,
    'baselineSettings.neighbourhoodRadius': policy.baselineSettings.neighbourhoodRadius,
    'baselineSettings.comparisonValueAggregateFunction': policy.baselineSettings.comparisonValueAggregateFunction,
    'baselineSettings.weekendAware': policy.baselineSettings.weekendAware
  };

  return isEqual(defaults, policyBaselineValues);
}

export function isExpressBaselineOverallSetting(policy) {
  return doesPolicyBaselineMatchDefaults(policy, getExpressBaselineOverallSettingValues());
}

export function isDefaultBaselineOverallSetting(policy) {
  return doesPolicyBaselineMatchDefaults(policy, getDefaultBaselineOverallSettingValues());
}

export function isPrecisionBaselineOverallSetting(policy) {
  return doesPolicyBaselineMatchDefaults(policy, getPrecisionBaselineOverallSettingValues());
}

export function getBaselineOverallSetting(policy) {
  if (isExpressBaselineOverallSetting(policy)) {
    return POLICY_BASELINE_PRESET_TYPES.EXPRESS;
  }

  if (isDefaultBaselineOverallSetting(policy)) {
    return POLICY_BASELINE_PRESET_TYPES.DEFAULT;
  }

  if (isPrecisionBaselineOverallSetting(policy)) {
    return POLICY_BASELINE_PRESET_TYPES.PRECISION;
  }

  return POLICY_BASELINE_PRESET_TYPES.CUSTOM;
}

export function getBaselineMinLookbackSeconds(value, unit) {
  if (unit === 'hours') {
    return (parseInt(value) || 0) * 60 * 60;
  }

  // it's days
  return (parseInt(value) || 0) * 60 * 60 * 24;
}

export function getBaselineMinLookbackValues(value) {
  try {
    const numericValue = Number(value.replace('s', ''));
    const isDays = numericValue % (60 * 60 * 24) === 0;
    const finalValue = Math.floor(isDays ? numericValue / (60 * 60 * 24) : numericValue / (60 * 60));

    return {
      minimal_length_unit: isDays ? 'days' : 'hours',
      minimalLength: finalValue
    };
  } catch (e) {
    return {
      minimal_length_unit: 'days',
      minimalLength: 4
    };
  }
}

export function getSummarySelectLabel(field, value, options) {
  value = value || field.getValue();
  const option = (options || field.options || []).find((o) => o.value === value);

  return option && option.label ? option.label : option;
}

export function getSummarySelectLabels(field, evaluateNumericalIds, options) {
  const labels = [];
  const opts = options || field.options;

  let values = field.getValue();

  if (values === null || values === undefined) {
    values = [];
  }

  if (!Array.isArray(values)) {
    const item = getSummarySelectLabel(field, values, options);

    if (item) {
      labels.push(item);
    }
  } else {
    values.forEach((value) => {
      const item =
        opts &&
        opts.filter((option) => {
          value = typeof value === 'object' ? value.value : value;
          if (evaluateNumericalIds) {
            return option.value === parseInt(value);
          }
          return option.value === value;
        })[0];

      if (item) {
        labels.push(item);
      }
    });
  }

  return (
    <Fragment>
      {labels.map((item) => (
        <Fragment key={uniqueId()}>
          {item.label && item.label}
          {!item.label && item}
        </Fragment>
      ))}
      {labels.length === 0 && 'None'}
    </Fragment>
  );
}

export function isEventPolicy(application) {
  return checkApplication.isEvent(application);
}

export function isSyslogEventPolicy(dimensions) {
  return dimensions.some((dimension) => dimension.includes(`__event_${EVENT_POLICY_TYPES.SYSLOG}__`));
}

export function isSnmpTrapEventPolicy(dimensions) {
  return dimensions.some((dimension) => dimension.includes(`__event_${EVENT_POLICY_TYPES.SNMP_TRAP}__`));
}

export function getDefaultEventAppProtoFilter(dimensions) {
  const eventType = isSnmpTrapEventPolicy(dimensions) ? EVENT_POLICY_TYPES.SNMP_TRAP : EVENT_POLICY_TYPES.SYSLOG;
  const filterField = `ktappprotocol__event_${eventType}__INT64_00`;

  return { filterField, operator: 'greaterThan', filterValue: '0' };
}

export function getPolicyManageUrl(policyType, policyId) {
  const pathname = '/v4/alerting/policies';
  const id = policyId || 'new';

  return `${pathname}/${id}`;
}

export function getMetricPolicyChartingConfig(metricConfig, queryConfig = {}) {
  const metricOptions = $metrics.getAvailableMetricOptions(metricConfig.selectedMeasurement);
  const queryOptions = { ...metricConfig };

  const { selectedDimensions, selectedMetrics, selectedRollupsAggFunc, selectedVisualization } = queryOptions;

  const measurement = $metrics.measurementModel(metricConfig.selectedMeasurement);

  if (!measurement) {
    return {};
  }

  const query =
    measurement &&
    $metrics.getFullMetricsQuery({
      metrics: selectedMetrics,
      queryOptions,
      measurement: measurement.label,
      allowMultiAggregate: false,
      ...queryConfig
    });

  return {
    availableMetricOptions: metricOptions,
    forceRunQuery: true,
    measurement,
    fullSelectedMeasurement: measurement,
    query,
    dimensions: selectedDimensions,
    metrics: selectedMetrics,
    selectedRollupsAggFunc,
    selectedVisualization
  };
}

export function getMaxKeysPerPolicy(policyApplication) {
  const topLevelApp = getTopLevelApplication(policyApplication);
  const maxKeysPermission = checkApplication.isMetric(topLevelApp) ? 'keysPerMetricPolicy' : 'keysPerPolicy';
  const permissionMax = $auth.getPermission(`alerts.limits.${maxKeysPermission}`);
  let maxKeys = permissionMax;
  if (!permissionMax && permissionMax !== 0) {
    const defaultMaxKeys = MAX_KEYS_PER_POLICY[topLevelApp] || MAX_KEYS_PER_POLICY.default;
    maxKeys = defaultMaxKeys;
  }
  return maxKeys;
}

export function convertThresholdFiltersToQueryFilters(thresholdFilters = {}) {
  const conjunctions = thresholdFilters.conjunctions || [];

  const filterGroups = conjunctions.map((conjunction) => {
    const { entries = [] } = conjunction;
    const enumMatchEntries = [];
    const stringMatchEntries = [];

    // We need to convert multi match entries into sub filtergroups, so we split them up here
    entries.forEach((entry) => {
      if (entry?.values?.length > 0) {
        enumMatchEntries.push(entry);
      } else if (entry?.patterns?.length > 0) {
        stringMatchEntries.push(entry);
      }
    });

    return buildFilterGroup({
      filterGroups: enumMatchEntries.map((entry) => {
        const { dimension, not, values: enumValues } = entry;

        // This might look inverted, but let me explain:
        // If you convert "Do not match the following values: A, B, C" into filter groups,
        // that actually means: "All of the following must be true: not A, not B, not C".
        const operator = not ? '<>' : '=';
        const connector = not ? 'All' : 'Any';

        // Build subfilterGroup
        return buildFilterGroup({
          connector,
          filters: enumValues.map((value) => ({
            filterField: dimension,
            operator,
            filterValue: value
          }))
        });
      }),
      filters: stringMatchEntries.map((entry) => {
        const { dimension, patterns, not } = entry || {};
        const pattern = patterns[0];

        return {
          filterField: dimension,
          operator: not ? '!~* ' : '~*', // regex matches
          filterValue: pattern
        };
      })
    });
  });

  return {
    connector: 'Any',
    filterGroups
  };
}
